import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  Pagination,
  Alert,
  CircularProgress,
  Button,
} from '@mui/material';
import { SearchResultsContainer } from './SearchResultsStyled';

export interface SearchResult {
  id: number;
  tipo: 'ARTIGO' | 'CURSO' | 'AULA' | 'VIDEO' | 'TOPICO';
  titulo: string;
  texto: string;
  search_score?: number;
  ref1_id?: number;
  ref1_slug?: string;
  ref1_tipo?: string;
  ref1_titulo?: string;
  ref2_id?: number;
  ref2_slug?: string;
  ref2_tipo?: string;
  ref2_titulo?: string;
}

export interface SearchResultsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: SearchResult[];
  error?: boolean;
  message?: string;
  hasMore?: boolean; // Flag para indicar se há mais resultados
  originalQuery?: string; // Query original para navegação
}

export interface Props {
  results: SearchResultsResponse | null;
  loading?: boolean;
  onPageChange?: (page: number) => void;
  currentPage?: number;
  onItemClick?: (item: SearchResult) => void;
}

const getTypeColor = (type: string) => {
  const colors = {
    'ARTIGO': 'primary',
    'CURSO': 'secondary',
    'AULA': 'success',
    'VIDEO': 'info',
    'TOPICO': 'warning',
  } as const;

  return colors[type as keyof typeof colors] || 'default';
};

const getTypeLabel = (type: string) => {
  const labels = {
    'ARTIGO': 'ARTIGO',
    'CURSO': 'CURSO',
    'AULA': 'AULA',
    'VIDEO': 'VIDEO',
    'TOPICO': 'NOTÍCIA',
  } as const;

  return labels[type as keyof typeof labels] || type;
};

const formatText = (text: string, maxLength: number = 150) => {
  return text.length > maxLength
    ? `${text.substring(0, maxLength)}...`
    : text;
};

const SearchResults: React.FC<Props> = ({
  results,
  loading = false,
  onPageChange,
  currentPage = 1,
  onItemClick,
}) => {
  if (loading) {
    return (
      <SearchResultsContainer>
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Buscando...
          </Typography>
        </Box>
      </SearchResultsContainer>
    );
  }

  if (!results) {
    return null;
  }

  if (results.error) {
    return (
      <SearchResultsContainer>
        <Alert severity="error">
          {results.message || 'Erro ao realizar busca'}
        </Alert>
      </SearchResultsContainer>
    );
  }

  if (results.results.length === 0) {
    return (
      <SearchResultsContainer>
        <Alert severity="info">
          Nenhum resultado encontrado para sua busca.
        </Alert>
      </SearchResultsContainer>
    );
  }

  const totalPages = Math.ceil(results.count / 20); // Assumindo 20 itens por página

  return (
    <SearchResultsContainer>
      <Box mb={2}>
        <Typography variant="h6" color="textSecondary">
          {results.count} resultado{results.count !== 1 ? 's' : ''} encontrado{results.count !== 1 ? 's' : ''}
        </Typography>
      </Box>

      <List>
        {results.results.map((item) => (
          <ListItem
            key={item.id}
            sx={{
              mb: 2,
              p: 0,
            }}
          >
            <Card
              sx={{
                width: '100%',
                cursor: onItemClick ? 'pointer' : 'default',
                '&:hover': onItemClick ? { elevation: 3 } : {},
              }}
              onClick={() => onItemClick?.(item)}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Typography variant="h6" component="h3" sx={{ flexGrow: 1, pr: 2 }}>
                    {item.titulo}
                  </Typography>
                  <Chip
                    label={getTypeLabel(item.tipo)}
                    color={getTypeColor(item.tipo)}
                    size="small"
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" paragraph dangerouslySetInnerHTML={{ __html: formatText(item.texto) }} />

                {(item.ref1_titulo || item.ref2_titulo) && (
                  <Box>
                    {item.ref1_titulo && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {item.ref1_tipo}: {item.ref1_titulo}
                      </Typography>
                    )}
                    {item.ref2_titulo && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {item.ref2_tipo}: {item.ref2_titulo}
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </ListItem>
        ))}
      </List>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => onPageChange?.(page)}
            sx={{ '& .MuiPaginationItem-root.Mui-selected': { backgroundColor: '#013a7b' } }}
            size="large"
          />
        </Box>
      )}
    </SearchResultsContainer>
  );
};

export { SearchResults };